import React, { useState, useEffect, useCallback } from "react";
import { Table } from "react-bootstrap";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import { useOpsContext } from "../../../ContextsAndProvider/OpsContext";
import { axios } from "../../../lib/axios";
import moment from "moment";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import { CalendarMonth, ListAlt } from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import InfoIconOutlined from "@mui/icons-material/InfoOutlined";
import { eventType, dashTypes } from "../../apps/AppointmentRequests/Constants";
import { ViewAppointmentModal } from "../../apps/AppointmentRequests/ViewAppointmentModal";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import Video from "../../../assets/images/Video.svg";
import Audio from "../../../assets/images/Audio.svg";
import "../../apps/AppointmentRequests/style.css";
import "./OpsDashboard.css";

const rowsPerPage = 10;

async function fetchDashboardApps(practitionerId, status) {
  const fetchAppointments = (nstatus) =>
    axios.get(`appointment?practitionerId=${practitionerId}&status=${nstatus}`);
  const requestedRes = await fetchAppointments("proposed");
  const proposedRes = await fetchAppointments("booked");
  let reqApps = [];
  if (status !== "proposed" && status !== "booked") {
    const res = await fetchAppointments(status);
    reqApps = res?.data || [];
  }
  return {
    proposed: proposedRes?.data || [],
    requested: requestedRes?.data || [],
    otherApps: reqApps,
  };
}

const OpsAppointmentsTab = () => {
  const { selectedOrganization, selectedPractitioner } = useOpsContext();
  const practitionerId = selectedPractitioner?.value;

  const [appointments, setAppointments] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnActive, setBtnActive] = useState("today");
  const [calendarView, setCalendarView] = useState(false);
  const [requestedCount, setRequestedCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [todayAppts, setTodayAppts] = useState([]);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  const [requestedAppts, setRequestedAppts] = useState([]);
  const [rejectedAppts, setRejectedAppts] = useState([]);
  const [completedAppts, setCompletedAppts] = useState([]);
  const [page, setPage] = useState(0);
  const [showAppointment, setShowAppointment] = useState(false);

  const loadAll = useCallback(async () => {
    if (!practitionerId) return;
    setLoading(true);
    try {
      const [base, cancelledRes, completedRes] = await Promise.all([
        fetchDashboardApps(practitionerId, "booked"),
        axios.get(`appointment?practitionerId=${practitionerId}&status=cancelled`).then((r) => r?.data || []),
        axios.get(`appointment?practitionerId=${practitionerId}&status=completed`).then((r) => r?.data || []),
      ]);
      const bookedApps = base?.proposed || [];
      const requestApps = base?.requested || [];
      const cancelledApps = Array.isArray(cancelledRes) ? cancelledRes : [];
      const completedApps = Array.isArray(completedRes) ? completedRes : [];

      setRequestedCount(requestApps.length);
      setRejectedCount(cancelledApps.length);
      setCompletedCount(completedApps.length);
      setRequestedAppts(requestApps);
      setRejectedAppts(cancelledApps);
      setCompletedAppts(completedApps);

      const today = moment().startOf("day");
      const todaysAppt = bookedApps
        .filter((d) => moment(d?.requestedPeriod?.[0]?.start).isSame(today, "day"))
        .sort((a, b) => moment(a?.requestedPeriod?.[0]?.start) - moment(b?.requestedPeriod?.[0]?.start));
      const upcomingAppt = bookedApps
        .filter(
          (d) =>
            moment(d?.requestedPeriod?.[0]?.start).isSameOrAfter(today, "day") &&
            !moment(d?.requestedPeriod?.[0]?.start).isSame(today, "day")
        )
        .sort((a, b) => moment(a?.requestedPeriod?.[0]?.start) - moment(b?.requestedPeriod?.[0]?.start));

      setTodayAppts(todaysAppt);
      setUpcomingAppts(upcomingAppt);
      setTodayCount(todaysAppt.length);
      setUpcomingCount(upcomingAppt.length);

      let nextData = [];
      if (btnActive === "today") nextData = todaysAppt;
      else if (btnActive === "upcoming") nextData = upcomingAppt;
      else if (btnActive === "requested") nextData = requestApps;
      else if (btnActive === "rejected") nextData = cancelledApps;
      else if (btnActive === "completed") nextData = completedApps;
      setAppointments(nextData);
      setFilterData(nextData);
    } catch {
      setAppointments([]);
      setFilterData([]);
    } finally {
      setLoading(false);
    }
  }, [practitionerId, btnActive]);

  useEffect(() => {
    if (!practitionerId) {
      setAppointments([]);
      setFilterData([]);
      return;
    }
    loadAll();
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, [practitionerId, loadAll]);

  const handleBtnChange = (val) => {
    setPage(0);
    setBtnActive(val);
    let nextData = [];
    if (val === "today") nextData = todayAppts;
    else if (val === "upcoming") nextData = upcomingAppts;
    else if (val === "requested") nextData = requestedAppts;
    else if (val === "rejected") nextData = rejectedAppts;
    else if (val === "completed") nextData = completedAppts;
    setAppointments(nextData);
    setFilterData(nextData);
  };

  const totalRows = filterData.length;
  const paginated = filterData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!selectedOrganization || !selectedPractitioner) return null;

  const allAppointmentsForModal = [
    ...todayAppts,
    ...upcomingAppts,
    ...requestedAppts,
    ...rejectedAppts,
    ...completedAppts,
  ].filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i);

  return (
    <div className="ops-dashboard-content">
      <div className="dash-buttons">
        <ButtonGroup>
          <Button
            variant="secondary"
            active={btnActive === "today"}
            onClick={() => handleBtnChange("today")}
          >
            Today ({todayCount})
          </Button>
          <Button
            variant="secondary"
            active={btnActive === "upcoming"}
            onClick={() => handleBtnChange("upcoming")}
          >
            Upcoming ({upcomingCount})
          </Button>
          <Button
            variant="secondary"
            active={btnActive === "requested"}
            onClick={() => handleBtnChange("requested")}
          >
            Requested ({requestedCount})
          </Button>
          <Button
            variant="secondary"
            active={btnActive === "rejected"}
            onClick={() => handleBtnChange("rejected")}
          >
            Rejected ({rejectedCount})
          </Button>
          <Button
            variant="secondary"
            active={btnActive === "completed"}
            onClick={() => handleBtnChange("completed")}
          >
            Completed ({completedCount})
          </Button>
        </ButtonGroup>
        <div style={{ alignSelf: "start" }}>
          <Tooltip title="Calendar View">
            <CalendarMonth
              onClick={() => setCalendarView(true)}
              style={{
                color: calendarView ? "#0055aa" : "#000000",
                marginRight: "1rem",
                cursor: "pointer",
              }}
            />
          </Tooltip>
          <Tooltip title="List View">
            <ListAlt
              onClick={() => setCalendarView(false)}
              style={{ color: calendarView ? "#000000" : "#0055aa", cursor: "pointer" }}
            />
          </Tooltip>
        </div>
      </div>

      {calendarView ? (
        <FullCalendar
          plugins={[timeGridPlugin, dayGridPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          events={
            appointments?.length > 0 &&
            appointments.map((appointmentData) => ({
              id: appointmentData.id,
              title: appointmentData.description,
              start: moment(appointmentData?.requestedPeriod?.[0]?.start).toDate(),
              end: moment(appointmentData?.requestedPeriod?.[0]?.end).toDate(),
              backgroundColor: eventType?.[appointmentData.status]?.backgroundColor,
              borderColor: eventType?.[appointmentData.status]?.backgroundColor,
              textColor: eventType?.[appointmentData.status]?.textColor,
              extendedProps: {
                serviceCategory: appointmentData?.serviceCategory?.[0]?.coding?.[0]?.display,
              },
            }))
          }
          eventDisplay="block"
          eventClick={(e) => setShowAppointment(e.event.id)}
          eventContent={(eventInfo) => {
            const start = eventInfo.event.start;
            const end = eventInfo.event.end;
            if (!start || !end) return <div>Time not available</div>;
            const serviceCategory = eventInfo.event.extendedProps?.serviceCategory;
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginLeft: "5px",
                }}
              >
                <span>
                  <b>{moment(start).format("HH:mm") + "-" + moment(end).format("HH:mm")}</b>
                </span>
                {serviceCategory && (
                  <Tooltip title={serviceCategory} placement="top">
                    <InfoIconOutlined
                      style={{ fontSize: "medium", marginRight: "5px", cursor: "pointer" }}
                    />
                  </Tooltip>
                )}
              </div>
            );
          }}
        />
      ) : (
        <>
          <div className="table-wrap">
            <Table responsive>
              <thead>
                <tr>
                  <th>Patients Name</th>
                  <th>Reason</th>
                  <th>Date Of Birth</th>
                  <th>Patient Identifier</th>
                  <th>Appointment Time</th>
                  <th style={{ textAlign: "center" }}>Communication Channel</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [1, 2, 3, 4, 5, 6].map((val) => (
                    <tr key={val}>
                      {[1, 2, 3, 4, 5, 6].map((cellIdx) => (
                        <td key={cellIdx}>
                          <Skeleton animation="wave" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginated?.length > 0 ? (
                  paginated.map((data) => {
                    const patient = data?.participant?.find((p) => p?.actor?.type === "Patient")?.actor;
                    const name = patient?.display || "-";
                    const patientId = patient?.reference?.split("/")?.[1];
                    return (
                      <tr
                        key={data?.id}
                        onClick={() => setShowAppointment(data?.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <span
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              textTransform: "capitalize",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (patientId)
                                window.open(
                                  `${window.location.origin}/app/patients-details?patientId=${patientId}`,
                                  "_blank"
                                );
                            }}
                          >
                            {name}
                          </span>
                        </td>
                        <td>{data?.description || "-"}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>{`${moment(data?.requestedPeriod?.[0]?.start).format("MMM-DD-YYYY HH:mm").toUpperCase()} - ${moment(data?.requestedPeriod?.[0]?.end).format("HH:mm")}`}</td>
                        <td>
                          <div className="action-wrap" style={{ alignItems: "center" }}>
                            {data?.intakeQuestions?.preferredChannel === "phone" ||
                            data?.intakeQuestions?.preferredChannel === "Phone" ? (
                              <Tooltip title={data?.intakeQuestions?.callerNumber}>
                                <img src={Audio} alt="Audio Channel" />
                              </Tooltip>
                            ) : (
                              <Tooltip title={data?.intakeQuestions?.preferredChannel}>
                                <img src={Video} alt="Video Channel" />
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <TableCell colSpan={6} style={{ textAlign: "center" }}>
                      No Data Available
                    </TableCell>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {filterData?.length > 0 && (
            <CustomPagination
              tableData={filterData}
              totalRows={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              handlePage={(pageNo) => setPage(pageNo)}
            />
          )}
        </>
      )}

      {showAppointment && (
        <ViewAppointmentModal
          modalShow={showAppointment}
          handleShow={() => setShowAppointment(false)}
          handleConnect={() => {}}
          handleCall={() => {}}
          handleUpdateRequest={() => {}}
          appointmentsList={allAppointmentsForModal}
          readOnly={true}
        />
      )}
    </div>
  );
};

export default OpsAppointmentsTab;
